import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import { acesso, type PasswordRecoveryReceipt } from '@/shared/api/resources';
import { ErrorCode } from '@/shared/api/types';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';

import { PublicFrame } from './PublicFrame';
import { usePortalConfig } from './usePortalConfig';

/**
 * Recuperacao de senha (o /recuperacao-da-senha do OnPoint).
 *
 * A resposta e a mesma exista ou nao a conta, e a tela diz o mesmo para todos. A
 * plataforma ainda nao envia e-mail: quando o servidor diz que a entrega automatica
 * nao esta ligada, a tela diz isso em vez de sugerir que uma mensagem saiu, como o
 * convite de acesso ja faz.
 */
export function PasswordRecoveryPage() {
  const [parametros] = useSearchParams();
  const empresa = parametros.get('empresa') ?? undefined;
  const { portal } = usePortalConfig(empresa);

  const [email, setEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [recibo, setRecibo] = useState<PasswordRecoveryReceipt | null>(null);

  const voltar = empresa ? `/entrar/${encodeURIComponent(empresa)}` : '/entrar';

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    void pedir();
  }

  async function pedir() {
    setErro(null);
    setEnviando(true);
    try {
      setRecibo(await acesso.requestRecovery(email));
    } catch (causa) {
      if (causa instanceof ApiError && causa.code === ErrorCode.RateLimited) {
        setErro(
          'Muitos pedidos para este e-mail em pouco tempo. Aguarde alguns minutos.',
        );
      } else if (
        causa instanceof ApiError &&
        causa.code === ErrorCode.ValidationFailed
      ) {
        setErro('Informe um e-mail valido.');
      } else {
        setErro('Nao foi possivel registrar o pedido. Tente novamente.');
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <PublicFrame portal={portal}>
      <h1 className="text-xl font-semibold text-ink-900">Recuperacao de Senha</h1>
      <p className="mt-1 mb-7 text-sm text-ink-500">
        Informe o e-mail da sua conta para receber o link de troca de senha.
      </p>

      {recibo ? (
        <div className="flex flex-col gap-4">
          <Alert tone="success" title="Pedido registrado">
            Se houver uma conta com este e-mail, ela recebe um link para criar uma senha
            nova, valido por {recibo.validity_minutes} minutos e que serve uma vez so.
          </Alert>
          {recibo.email_delivery_available ? null : (
            <Alert tone="warning" title="Envio automatico desligado">
              Esta plataforma ainda nao envia e-mail automaticamente. O pedido ficou
              registrado, mas o link nao chega por e-mail. Para recuperar o acesso
              agora, fale com quem administra o acesso da sua empresa.
            </Alert>
          )}
          <Link
            to={voltar}
            className="text-center text-sm font-medium text-brand-600 hover:underline"
          >
            Voltar para o acesso
          </Link>
        </div>
      ) : (
        <form onSubmit={enviar} noValidate className="flex flex-col gap-4">
          {erro ? <Alert tone="danger">{erro}</Alert> : null}

          <Field
            label="E-mail do usuario"
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(evento) => setEmail(evento.target.value)}
            placeholder="Digite seu e-mail"
          />

          <div className="mt-2 flex gap-3">
            <Link
              to={voltar}
              className="inline-flex h-12 flex-1 items-center justify-center rounded-md border border-line-strong text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              Voltar
            </Link>
            <Button type="submit" size="lg" loading={enviando} className="flex-[2]">
              Enviar link de recuperacao
            </Button>
          </div>
        </form>
      )}
    </PublicFrame>
  );
}
